import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Download, Search, Play, Pause, Lock, Filter, Tag } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { StatusBadge } from '../components/StatusBadge';
export function MailingListDetail() {
  const {
    listId
  } = useParams<{
    listId: string;
  }>();
  const {
    newsletters,
    getSubscriptionsByMailingList,
    getSubmissionsByMailingList,
    forms
  } = useWorkspace();
  const [activeTab, setActiveTab] = useState<'subscribers' | 'submissions'>('subscribers');
  const [searchQuery, setSearchQuery] = useState('');
  const list = newsletters.find(n => n.id === parseInt(listId || '0'));
  const subscribers = getSubscriptionsByMailingList(parseInt(listId || '0'));
  const submissions = getSubmissionsByMailingList(parseInt(listId || '0'));
  const filteredSubscribers = subscribers.filter(s => s.email.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSubmissions = submissions.filter(s => s.email.toLowerCase().includes(searchQuery.toLowerCase()));
  const getIntakeStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return {
          icon: Play,
          color: 'text-green-600',
          bg: 'bg-green-50',
          label: 'Accepting Signups'
        };
      case 'paused':
        return {
          icon: Pause,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          label: 'Paused'
        };
      case 'closed':
        return {
          icon: Lock,
          color: 'text-red-600',
          bg: 'bg-red-50',
          label: 'Closed'
        };
      default:
        return {
          icon: Play,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          label: 'Unknown'
        };
    }
  };
  if (!list) {
    return <div className="text-center py-12" data-id="element-1229">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-1230">
          Mailing list not found
        </h2>
        <Link to="/mailing-lists" className="text-blue-600 hover:text-blue-800 mt-2 inline-block" data-id="element-1231">
          ← Back to Mailing Lists
        </Link>
      </div>;
  }
  const intakeConfig = getIntakeStatusConfig(list.intakeStatus);
  const IntakeIcon = intakeConfig.icon;
  const linkedForm = list.formId ? forms.find(f => f.id === list.formId) : null;
  return <div className="space-y-6" data-id="element-1232">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-1233">
        <div className="flex items-start gap-4" data-id="element-1234">
          <Link to="/mailing-lists" className="p-2 hover:bg-gray-100 rounded-full text-gray-500 mt-1" data-id="element-1235">
            <ArrowLeft className="h-5 w-5" data-id="element-1236" />
          </Link>
          <div data-id="element-1237">
            <div className="flex items-center gap-3" data-id="element-1238">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1239">
                {list.name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${intakeConfig.bg} ${intakeConfig.color}`} data-id="element-1240">
                <IntakeIcon className="h-3 w-3" data-id="element-1241" />
                {intakeConfig.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1242">{list.description}</p>
          </div>
        </div>
        <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-1243">
          <Download className="h-4 w-4" data-id="element-1244" />
          Export
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4" data-id="element-1245">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1246">
          <div className="flex items-center gap-3" data-id="element-1247">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-1248">
              <Users className="h-5 w-5 text-blue-600" data-id="element-1249" />
            </div>
            <div data-id="element-1250">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1251">
                {list.subscriberCount.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1252">Total Subscribers</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1253">
          <div className="flex items-center gap-3" data-id="element-1254">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-1255">
              <Mail className="h-5 w-5 text-green-600" data-id="element-1256" />
            </div>
            <div data-id="element-1257">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1258">
                {subscribers.filter(s => s.status === 'confirmed').length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1259">Confirmed</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1260">
          <div className="flex items-center gap-3" data-id="element-1261">
            <div className="p-2 bg-yellow-50 rounded-lg" data-id="element-1262">
              <Mail className="h-5 w-5 text-yellow-600" data-id="element-1263" />
            </div>
            <div data-id="element-1264">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1265">
                {subscribers.filter(s => s.status === 'pending').length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1266">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1267">
          <div className="flex items-center gap-3" data-id="element-1268">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-1269">
              <Mail className="h-5 w-5 text-purple-600" data-id="element-1270" />
            </div>
            <div data-id="element-1271">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1272">
                {submissions.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1273">Form Submissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Linked Form Info */}
      {linkedForm && <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-center justify-between" data-id="element-1274">
          <div className="flex items-center gap-3" data-id="element-1275">
            <Mail className="h-5 w-5 text-blue-600" data-id="element-1276" />
            <div data-id="element-1277">
              <p className="text-sm font-medium text-blue-900" data-id="element-1278">
                Linked Signup Form
              </p>
              <p className="text-sm text-blue-700" data-id="element-1279">{linkedForm.name}</p>
            </div>
          </div>
          <Link to={`/forms/${linkedForm.id}`} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1280">
            View Form →
          </Link>
        </div>}

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-1281">
        <nav className="-mb-px flex gap-6" data-id="element-1282">
          <button onClick={() => setActiveTab('subscribers')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'subscribers' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1283">
            Subscribers ({subscribers.length})
          </button>
          <button onClick={() => setActiveTab('submissions')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1284">
            Form Submissions ({submissions.length})
          </button>
        </nav>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3" data-id="element-1285">
        <div className="relative flex-1 max-w-md" data-id="element-1286">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-1287" />
          <input type="text" placeholder="Search by email..." className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1288" />
        </div>
        <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-1289">
          <Filter className="h-4 w-4" data-id="element-1290" />
          Filter
        </button>
      </div>

      {/* Content */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1291">
        {activeTab === 'subscribers' ? filteredSubscribers.length === 0 ? <div className="p-12 text-center" data-id="element-1292">
              <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-1293" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-1294">
                No subscribers found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-1295">
                Subscribers will appear here once they confirm their
                subscription.
              </p>
            </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-1296">
              <thead className="bg-gray-50" data-id="element-1297">
                <tr data-id="element-1298">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1299">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1300">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1301">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1302">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1303">
                {filteredSubscribers.map(sub => <tr key={sub.id} className="hover:bg-gray-50" data-id="element-1304">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1305">
                      {sub.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1306">
                      <StatusBadge status={sub.status} data-id="element-1307" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1308">
                      <div className="flex flex-wrap gap-1" data-id="element-1309">
                        {sub.tags?.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-id="element-1310">
                            <Tag className="h-3 w-3" data-id="element-1311" />
                            {tag}
                          </span>)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1312">
                      {sub.joined}
                    </td>
                  </tr>)}
              </tbody>
            </table> : filteredSubmissions.length === 0 ? <div className="p-12 text-center" data-id="element-1313">
            <Mail className="mx-auto h-12 w-12 text-gray-400" data-id="element-1314" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-1315">
              No submissions found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1316">
              Form submissions for this list will appear here.
            </p>
          </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-1317">
            <thead className="bg-gray-50" data-id="element-1318">
              <tr data-id="element-1319">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1320">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1321">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1322">
                  Source Form
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1323">
                  Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1324">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1325">
              {filteredSubmissions.map(sub => <tr key={sub.id} className="hover:bg-gray-50" data-id="element-1326">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1327">
                    {sub.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1328">
                    {sub.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1329">
                    {sub.form}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1330">
                    <div className="flex flex-wrap gap-1" data-id="element-1331">
                      {sub.tags?.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-id="element-1332">
                          <Tag className="h-3 w-3" data-id="element-1333" />
                          {tag}
                        </span>)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1334">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sub.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1335">
                      {sub.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>
    </div>;
}